import { MantineThemeOverride } from '@mantine/core';
import { BRAND_COLORS, BRAND_PALETTES } from '../constants/colorPalette';

export const theme: MantineThemeOverride = {
  primaryColor: 'brand',
  // brand palette mixes warm (0-4 orange/red) and cool (5-9 purple) — force Mantine to always
  // resolve `color="brand"` to the purple range so dark-mode components don't auto-select the
  // red/orange indices and produce broken contrast.
  primaryShade: { light: 5, dark: 6 },
  fontFamily: '"Hurme Geometric Sans 3", sans-serif',
  fontFamilyMonospace: 'Monaco, Courier, monospace',
  headings: {
    fontFamily: '"Hurme Geometric Sans 3", sans-serif',
    fontWeight: '700',
  },
  colors: {
    // Main brand color palette
    brand: [
      BRAND_COLORS.orange, // 0: Primary Orange
      BRAND_COLORS.lightOrange, // 1: Light Orange
      BRAND_COLORS.darkerOrange, // 2: Darker Orange
      BRAND_COLORS.red, // 3: Red
      BRAND_COLORS.darkerRed, // 4: Darker Red
      BRAND_COLORS.purple, // 5: Purple
      BRAND_COLORS.lightPurple, // 6: Light Purple
      BRAND_COLORS.darkPurple, // 7: Dark Purple
      BRAND_COLORS.darkerPurple, // 8: Darker Purple
      BRAND_COLORS.darkestPurple, // 9: Darkest Purple
    ],

    // Sopra Steria specific color palettes
    sopraPrimary: [
      BRAND_PALETTES.red[50],
      BRAND_PALETTES.red[100],
      BRAND_PALETTES.red[200],
      BRAND_PALETTES.red[300],
      BRAND_PALETTES.red[400],
      BRAND_COLORS.red,
      BRAND_PALETTES.red[600],
      BRAND_PALETTES.red[700],
      BRAND_PALETTES.red[800],
      BRAND_PALETTES.red[900],
    ],

    sopraBlue: [
      BRAND_PALETTES.blue[50],
      BRAND_PALETTES.blue[100],
      BRAND_PALETTES.blue[200],
      BRAND_PALETTES.blue[300],
      BRAND_PALETTES.blue[400],
      BRAND_COLORS.blue,
      BRAND_PALETTES.blue[600],
      BRAND_PALETTES.blue[700],
      BRAND_PALETTES.blue[800],
      BRAND_PALETTES.blue[900],
    ],

    sopraOrange: [
      BRAND_PALETTES.orange[50],
      BRAND_PALETTES.orange[100],
      BRAND_PALETTES.orange[200],
      BRAND_PALETTES.orange[300],
      BRAND_PALETTES.orange[400],
      BRAND_COLORS.orange,
      BRAND_PALETTES.orange[600],
      BRAND_PALETTES.orange[700],
      BRAND_PALETTES.orange[800],
      BRAND_PALETTES.orange[900],
    ],

    sopraPurple: [
      BRAND_PALETTES.deepPurple[50],
      BRAND_PALETTES.deepPurple[100],
      BRAND_PALETTES.deepPurple[200],
      BRAND_PALETTES.deepPurple[300],
      BRAND_PALETTES.deepPurple[400],
      BRAND_COLORS.purple,
      BRAND_PALETTES.deepPurple[600],
      BRAND_PALETTES.deepPurple[700],
      BRAND_PALETTES.deepPurple[800],
      BRAND_PALETTES.deepPurple[900],
    ],

    sopraGreen: [
      BRAND_PALETTES.green[50],
      BRAND_PALETTES.green[100],
      BRAND_PALETTES.green[200],
      BRAND_PALETTES.green[300],
      BRAND_PALETTES.green[400],
      BRAND_COLORS.darkGreen,
      BRAND_PALETTES.green[600],
      BRAND_PALETTES.green[700],
      BRAND_PALETTES.green[800],
      BRAND_PALETTES.green[900],
    ],

    // Material Design palettes for comprehensive color support
    materialGray: [
      BRAND_PALETTES.gray[50],
      BRAND_PALETTES.gray[100],
      BRAND_PALETTES.gray[200],
      BRAND_PALETTES.gray[300],
      BRAND_PALETTES.gray[400],
      BRAND_PALETTES.gray[500],
      BRAND_PALETTES.gray[600],
      BRAND_PALETTES.gray[700],
      BRAND_PALETTES.gray[800],
      BRAND_PALETTES.gray[900],
    ],

    materialRed: [
      BRAND_PALETTES.red[50],
      BRAND_PALETTES.red[100],
      BRAND_PALETTES.red[200],
      BRAND_PALETTES.red[300],
      BRAND_PALETTES.red[400],
      BRAND_PALETTES.red[500],
      BRAND_PALETTES.red[600],
      BRAND_PALETTES.red[700],
      BRAND_PALETTES.red[800],
      BRAND_PALETTES.red[900],
    ],

    materialOrange: [
      BRAND_PALETTES.orange[50],
      BRAND_PALETTES.orange[100],
      BRAND_PALETTES.orange[200],
      BRAND_PALETTES.orange[300],
      BRAND_PALETTES.orange[400],
      BRAND_PALETTES.orange[500],
      BRAND_PALETTES.orange[600],
      BRAND_PALETTES.orange[700],
      BRAND_PALETTES.orange[800],
      BRAND_PALETTES.orange[900],
    ],

    materialAmber: [
      BRAND_PALETTES.amber[50],
      BRAND_PALETTES.amber[100],
      BRAND_PALETTES.amber[200],
      BRAND_PALETTES.amber[300],
      BRAND_PALETTES.amber[400],
      BRAND_PALETTES.amber[500],
      BRAND_PALETTES.amber[600],
      BRAND_PALETTES.amber[700],
      BRAND_PALETTES.amber[800],
      BRAND_PALETTES.amber[900],
    ],

    materialGreen: [
      BRAND_PALETTES.green[50],
      BRAND_PALETTES.green[100],
      BRAND_PALETTES.green[200],
      BRAND_PALETTES.green[300],
      BRAND_PALETTES.green[400],
      BRAND_PALETTES.green[500],
      BRAND_PALETTES.green[600],
      BRAND_PALETTES.green[700],
      BRAND_PALETTES.green[800],
      BRAND_PALETTES.green[900],
    ],

    materialTeal: [
      BRAND_PALETTES.teal[50],
      BRAND_PALETTES.teal[100],
      BRAND_PALETTES.teal[200],
      BRAND_PALETTES.teal[300],
      BRAND_PALETTES.teal[400],
      BRAND_PALETTES.teal[500],
      BRAND_PALETTES.teal[600],
      BRAND_PALETTES.teal[700],
      BRAND_PALETTES.teal[800],
      BRAND_PALETTES.teal[900],
    ],

    materialCyan: [
      BRAND_PALETTES.cyan[50],
      BRAND_PALETTES.cyan[100],
      BRAND_PALETTES.cyan[200],
      BRAND_PALETTES.cyan[300],
      BRAND_PALETTES.cyan[400],
      BRAND_PALETTES.cyan[500],
      BRAND_PALETTES.cyan[600],
      BRAND_PALETTES.cyan[700],
      BRAND_PALETTES.cyan[800],
      BRAND_PALETTES.cyan[900],
    ],

    materialPurple: [
      BRAND_PALETTES.purple[50],
      BRAND_PALETTES.purple[100],
      BRAND_PALETTES.purple[200],
      BRAND_PALETTES.purple[300],
      BRAND_PALETTES.purple[400],
      BRAND_PALETTES.purple[500],
      BRAND_PALETTES.purple[600],
      BRAND_PALETTES.purple[700],
      BRAND_PALETTES.purple[800],
      BRAND_PALETTES.purple[900],
    ],

    materialDeepPurple: [
      BRAND_PALETTES.deepPurple[50],
      BRAND_PALETTES.deepPurple[100],
      BRAND_PALETTES.deepPurple[200],
      BRAND_PALETTES.deepPurple[300],
      BRAND_PALETTES.deepPurple[400],
      BRAND_PALETTES.deepPurple[500],
      BRAND_PALETTES.deepPurple[600],
      BRAND_PALETTES.deepPurple[700],
      BRAND_PALETTES.deepPurple[800],
      BRAND_PALETTES.deepPurple[900],
    ],

    materialDeepOrange: [
      BRAND_PALETTES.deepOrange[50],
      BRAND_PALETTES.deepOrange[100],
      BRAND_PALETTES.deepOrange[200],
      BRAND_PALETTES.deepOrange[300],
      BRAND_PALETTES.deepOrange[400],
      BRAND_PALETTES.deepOrange[500],
      BRAND_PALETTES.deepOrange[600],
      BRAND_PALETTES.deepOrange[700],
      BRAND_PALETTES.deepOrange[800],
      BRAND_PALETTES.deepOrange[900],
    ],

    materialLightGreen: [
      BRAND_PALETTES.lightGreen[50],
      BRAND_PALETTES.lightGreen[100],
      BRAND_PALETTES.lightGreen[200],
      BRAND_PALETTES.lightGreen[300],
      BRAND_PALETTES.lightGreen[400],
      BRAND_PALETTES.lightGreen[500],
      BRAND_PALETTES.lightGreen[600],
      BRAND_PALETTES.lightGreen[700],
      BRAND_PALETTES.lightGreen[800],
      BRAND_PALETTES.lightGreen[900],
    ],
  },
  components: {
    Button: {
      defaultProps: { color: 'brand' },
      styles: {
        root: {
          fontFamily: '"Hurme Geometric Sans 3", sans-serif',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'var(--mantine-color-brand-1)',
          }
        }
      }
    },
    Card: {
      defaultProps: {
        withBorder: true,
      },
      styles: {
        root: {
          borderColor: 'var(--mantine-color-brand-1)',
        }
      }
    },
    Badge: {
      defaultProps: { color: 'brand' }
    },
    Tabs: {
      defaultProps: { color: 'brand' }
    },
    Switch: {
      defaultProps: { color: 'brand' }
    },
    Checkbox: {
      defaultProps: { color: 'brand' }
    },
    Radio: {
      defaultProps: { color: 'brand' }
    },
    TextInput: {
      styles: {
        input: {
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-0)',
          }
        }
      }
    },
    Select: {
      styles: {
        input: {
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-0)',
          }
        }
      }
    }
  }
} as const;

// Export types
export type AppTheme = typeof theme; 
